import React, { useState, useCallback, useRef, useEffect, useDeferredValue } from 'react';
import { Pagination, Icon, Button } from '@ann/web-components';
import { useNotesTabPage, useNotesGroupedByMonth } from './hooks';
import { useActivitySearch } from '../../context/ActivitySearchContext';
import { NoteCardV2, SelectionActionBar } from './components';
import NotesSlideOut from '../../../Activity/components/PreviewSlideOut/NotesSlideOut';
import {
  TabContainer,
  HeaderContainer,
  TitleSection,
  Title,
  ItemCount,
  ActionsContainer,
  ListContainer,
  MonthGroupHeader,
  NotesGroupSection,
  EmptyState,
  EmptyIcon,
  EmptyTitle,
  EmptyDescription,
  PaginationWrapper,
  PaginationInfo,
  PaginationText,
  LoadingContainer,
  SkeletonCard,
} from './NotesTab.styled';

const NotesGroupsList = React.memo(({
  notesGroupedByMonth,
  selectedIds,
  onSelect,
  onEdit,
  onPin,
  onArchiveToggle,
}) => (
  <ListContainer>
    {notesGroupedByMonth.map((group) => (
      <NotesGroupSection key={group.key}>
        {group.label != null && (
          <MonthGroupHeader>{group.label}</MonthGroupHeader>
        )}
        {group.notes.map((note) => (
          <NoteCardV2
            key={note.id}
            note={note}
            isSelected={selectedIds.has(note.id)}
            onSelect={onSelect}
            onEdit={onEdit}
            onPin={onPin}
            onArchiveToggle={onArchiveToggle}
          />
        ))}
      </NotesGroupSection>
    ))}
  </ListContainer>
));

const NotesTab = () => {
  const {
    notes,
    meta,
    loading,
    error,
    filters,
    selectedIds,
    handleSearchChange,
    toggleSelect,
    clearSelection,
    handlePageChange,
    handleItemsPerPageChange,
    handleRefresh,
    createNote,
    getNoteDetail,
    updateNote,
    pinNote,
    archiveNote,
  } = useNotesTabPage();

  const {
    searchValue: headerSearchValue,
    setSearchValue: setHeaderSearchValue,
    registerCreateNoteHandler,
  } = useActivitySearch();
  const deferredHeaderSearchValue = useDeferredValue(headerSearchValue);
  const searchTimeoutRef = useRef(null);

  // SlideOut state
  const [showCreateSlideOut, setShowCreateSlideOut] = useState(false);
  const [isLoadingUpdateSlideOut, setIsLoadingUpdateSlideOut] = useState(false);
  const [showUpdateSlideOut, setShowUpdateSlideOut] = useState(false);
  const [selectedNoteData, setSelectedNoteData] = useState(null);
  const [openInEditMode, setOpenInEditMode] = useState(false);

  // Sync header search (context) -> Notes tab filter (debounced)
  useEffect(() => {
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(() => {
      handleSearchChange(deferredHeaderSearchValue);
    }, 500);
    return () => {
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    };
  }, [deferredHeaderSearchValue, handleSearchChange]);

  // Sync tab filter (e.g. clear) -> header search
  useEffect(() => {
    if (filters.search !== headerSearchValue) {
      setHeaderSearchValue(filters.search);
    }
  }, [filters.search]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleAddNote = useCallback(() => {
    setShowCreateSlideOut(true);
  }, []);

  useEffect(() => {
    return registerCreateNoteHandler(handleAddNote);
  }, [registerCreateNoteHandler, handleAddNote]);

  // Handle Edit Note (view/update slideout)
  // Open immediately with list data so panel appears without waiting for getNoteDetail.
  // Then fetch detail in background and update when ready (fixes first-time delay).
  // options.openInEditMode: when true (e.g. "Edit" from card menu), open directly in edit mode.
  const handleEditNote = useCallback(
    async (note, options = {}) => {
      setOpenInEditMode(Boolean(options?.openInEditMode));
      setIsLoadingUpdateSlideOut(true);
      const listData = {
        ...note,
        note_title: note.title,
        note_content: note.description,
        ownerFullName: note.owner?.name,
        lastUpdate: note.lastUpdated,
      };
      setSelectedNoteData(listData);
      setShowUpdateSlideOut(true);
      try {
        const noteDetail = await getNoteDetail(note.noteUuid || note.id);
        const detailData = noteDetail?.body || noteDetail;
        
        // Merge: keep owner/date info from list, add detail content
        // The list API has owner/dates, detail API has full content
        const mergedData = {
          ...note,        // Original note from list (has owner, createdAt, lastUpdated)
          ...detailData,  // Detail response (has full content)
          // Ensure owner info from list is preserved
          ownerFullName: note.owner?.name || detailData.ownerFullName,
          createdAt: note.createdAt || detailData.createdAt,
          lastUpdate: note.lastUpdated || detailData.lastUpdate,
          category: note.category || detailData.category || 'GENERAL',
          raw: detailData.raw,
        };
        setSelectedNoteData(mergedData);
      } catch (err) {
        console.error('Error getting note detail:', err);
      } finally {
        setIsLoadingUpdateSlideOut(false);
      }
    },
    [getNoteDetail]
  );

  // Handle Pin Note - uses pinNote from context
  const handlePinNote = useCallback((noteId, pinned) => {
    pinNote(noteId, pinned);
  }, [pinNote]);

  // Handle SlideOut close
  const handleCreateSlideOutClose = useCallback(() => {
    setShowCreateSlideOut(false);
  }, []);

  const handleUpdateSlideOutClose = useCallback(() => {
    setShowUpdateSlideOut(false);
    setSelectedNoteData(null);
    setOpenInEditMode(false);
  }, []);

  // Handle Note Created
  const handleNoteCreated = useCallback(() => {
    setShowCreateSlideOut(false);
  }, []);

  // Handle Note Updated
  const handleNoteUpdated = useCallback(() => {
    setShowUpdateSlideOut(false);
    setSelectedNoteData(null);
  }, []);

  // Handle Archive
  const handleArchive = useCallback(() => {
    console.log('Archive notes:', Array.from(selectedIds));
    // TODO: Implement archive functionality
  }, [selectedIds]);

  const handleArchiveToggle = useCallback(
    (noteId, shouldArchive) => {
      archiveNote?.(noteId, shouldArchive);
      // TODO: Wire real archive/unarchive API endpoint for persistence.
    },
    [archiveNote]
  );
  const selectedCount = selectedIds.size;
  const hasSelection = selectedCount > 0;

  const notesGroupedByMonth = useNotesGroupedByMonth(notes);

  // Render loading skeleton
  if (loading && notes.length === 0) {
    return (
      <TabContainer>
        <LoadingContainer>
          {[1, 2, 3, 4, 5].map((i) => (
            <SkeletonCard key={i} />
          ))}
        </LoadingContainer>
      </TabContainer>
    );
  }

  // Empty state — sin notas
  if (!loading && !error && notes.length === 0) {
    return (
      <TabContainer>
        <EmptyState>
          <EmptyIcon>
            <Icon name="description" width="48px" height="48px" />
          </EmptyIcon>
          <EmptyTitle>No notes yet</EmptyTitle>
          <EmptyDescription>Create your first note to get started.</EmptyDescription>
          <Button
            label="Create note"
            type="primary"
            size="medium"
            iconLeft="plus"
            onClick={handleAddNote}
          />
        </EmptyState>
      </TabContainer>
    );
  }

  // Render error state
  if (error && notes.length === 0) {
    return (
      <TabContainer>
        <HeaderContainer>
          <TitleSection>
            <Title>Notes</Title>
          </TitleSection>
          <ActionsContainer>
            <Button
              label="Add note"
              type="primary"
              size="medium"
              iconLeft="plus"
              onClick={handleAddNote}
            />
          </ActionsContainer>
        </HeaderContainer>
        <EmptyState>
          <EmptyIcon>
            <Icon name="error" width="48px" height="48px" />
          </EmptyIcon>
          <EmptyTitle>Failed to load notes</EmptyTitle>
          <EmptyDescription>
            {error.message || 'An error occurred. Please try again.'}
          </EmptyDescription>
          <Button label="Retry" type="secondary" onClick={handleRefresh} />
        </EmptyState>
      </TabContainer>
    );
  }

  return (
    <TabContainer>
      {/* Header */}

      {/* Filters 
      
      <FiltersContainer>
        <SearchContainer>
          <TextField
            iconLeft="search"
            placeholder="Search notes..."
            value={localSearchValue}
            onChange={handleLocalSearchChange}
            size="small"
            disabled={loading}
          />
        </SearchContainer>
        <FilterDropdownWrapper>
          <Select
            placeholder="All Categories"
            value={filters.category}
            onChange={(selected) => handleCategoryChange(selected?.value || selected)}
            size="small"
            width="auto"
            minWidth="140px"
            disabled={loading}
          >
            <Select.Menu>
              {CATEGORY_OPTIONS.map((option) => (
                <Select.Item key={option.value} value={option.value} label={option.label}>
                  {option.label}
                </Select.Item>
              ))}
            </Select.Menu>
          </Select>
        </FilterDropdownWrapper>
      </FiltersContainer>
      */}

      {/* Selection Action Bar — flotante a 145px al hacer scroll */}
      {hasSelection && (
        <SelectionActionBar
          selectedCount={selectedCount}
          onArchive={handleArchive}
          onClearSelection={clearSelection}
        />
      )}

      {/* List: notas agrupadas por mes/año (mes actual sin encabezado, resto con "MONTH YEAR") */}
      <>
        <NotesGroupsList
          notesGroupedByMonth={notesGroupedByMonth}
          selectedIds={selectedIds}
          onSelect={toggleSelect}
          onEdit={handleEditNote}
          onPin={handlePinNote}
          onArchiveToggle={handleArchiveToggle}
        />

        {/* Pagination */}
        {notes.length > 0 && (
          <PaginationWrapper>
            <PaginationInfo>
              <PaginationText>
                Showing {(meta.current_page - 1) * meta.per_page + 1} to{' '}
                {Math.min(meta.current_page * meta.per_page, meta.total_items)} of{' '}
                {meta.total_items} notes
              </PaginationText>
              <Pagination
                currentPage={meta.current_page}
                itemsPerPage={meta.per_page}
                onItemsPerPageChange={handleItemsPerPageChange}
                onPageChange={handlePageChange}
                totalItems={meta.total_items}
                totalPages={meta.total_pages}
                disabled={loading}
              />
            </PaginationInfo>
          </PaginationWrapper>
        )}
      </>


      {/* Create Note SlideOut */}
      <NotesSlideOut
        open={showCreateSlideOut}
        onClose={handleCreateSlideOutClose}
        mode="create"
        onCreateNote={async (payload) => {
          await createNote(payload);
          handleNoteCreated();
        }}
      />

      {/* Update Note SlideOut */}
      <NotesSlideOut
        open={showUpdateSlideOut}
        onClose={handleUpdateSlideOutClose}
        mode="update"
        noteData={selectedNoteData}
        initialEditMode={openInEditMode}
        isLoading={isLoadingUpdateSlideOut}
        onUpdateNote={async (payload) => {
          await updateNote(payload);
          handleNoteUpdated();
        }}
      />
    </TabContainer>
  );
};

export default NotesTab;
